import React, { useState, useRef, useCallback } from 'react';
import { Upload } from 'lucide-react';
import { cn } from '../lib/utils';

export function DragDropZone({ 
  onFileSelect, 
  accept = '.pdf', 
  maxSize = 10 * 1024 * 1024,
  children, 
  className,
  disabled = false,
  'data-testid': testId 
}) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (maxSize && file.size > maxSize) {
        return;
      }
      onFileSelect(file);
    }
  }, [disabled, maxSize, onFileSelect]);

  const handleClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div
      className={cn(
        'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
        isDragging 
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30' 
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
      data-testid={testId}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />
      {isDragging ? (
        <div className="flex flex-col items-center gap-3">
          <Upload className="h-12 w-12 text-blue-500 animate-bounce" />
          <p className="text-sm font-medium text-blue-600">Drop file here</p>
        </div>
      ) : (
        children || (
          <div className="flex flex-col items-center gap-3">
            <Upload className="h-12 w-12 text-slate-400" />
            <p className="text-sm font-medium">Drag & drop or click to upload</p>
            <p className="text-xs text-muted-foreground">PDF files up to 10MB</p>
          </div>
        )
      )}
    </div>
  );
}
